import {
  IStatementCurrencySection,
  IStatementLine,
  StatementLineType,
  currencyFormatter,
  toDecimal,
} from "shared";
import { When } from "react-if";
import { Tooltip } from "@mui/material";
import { ExclamationCircleIcon } from "@heroicons/react/20/solid";

import { CardContent } from "../../../../../../common/Atoms/CardContent";
import { classNames } from "../../../../../../common/lib/classNames";
import { WrappedLink } from "../../../../WrappedLink";
import { LoadingOverlay } from "../../../../Molecules/LoadingOverlay";
import { DatePopover } from "../../../../../../common/Components/DatePopover";
import { Badge } from "../../../../../../common/Atoms/Badge";

interface IActivityStatementProps {
  currencySection: IStatementCurrencySection;
  loading?: boolean;
  children?: React.ReactNode;
  organisationTimezone: string;
}

// Line types where the amount column is blank on activity statements (matches the PDF renderer)
const blankAmountTypes = [
  StatementLineType.OpeningBalance,
  StatementLineType.Payment,
  StatementLineType.Overpayment,
  StatementLineType.CreditNoteRefund,
  StatementLineType.OverpaymentRefund,
];

const paymentColumnTypes = [
  StatementLineType.Payment,
  StatementLineType.Overpayment,
  StatementLineType.Prepayment,
];

const refundTypes = [
  StatementLineType.PrepaymentRefund,
  StatementLineType.CreditNoteRefund,
  StatementLineType.OverpaymentRefund,
];

export function ActivityStatement({
  currencySection: statement,
  loading,
  children,
  organisationTimezone,
}: IActivityStatementProps) {

  if (!statement) {
    return null;
  }

  function formatAmount(amount: number, currency: string, opts?: { removeNegative?: boolean; bracketsOnNegative?: boolean }) {
    const formatter = currencyFormatter(currency);

    let result = formatter(toDecimal(amount));

    if (opts?.removeNegative || opts?.bracketsOnNegative) {
      result = result.replace(`-`, ``);
    }

    if (amount < 0 && opts?.bracketsOnNegative) {
      return `(${result})`;
    }

    return result;
  }

  function amountCell(line: IStatementLine) {
    if (blankAmountTypes.includes(line.type)) {
      return ``;
    }

    if (line.type === StatementLineType.Prepayment) {
      return formatAmount(line.totalInCents, line.currency, { removeNegative: true });
    }

    return formatAmount(line.totalInCents, line.currency, { bracketsOnNegative: true });
  }

  function paymentCell(line: IStatementLine) {
    if (refundTypes.includes(line.type)) {
      return formatAmount(line.paymentsInCents, line.currency, { removeNegative: true, bracketsOnNegative: true });
    }

    if (paymentColumnTypes.includes(line.type)) {
      return formatAmount(line.paymentsInCents, line.currency, { removeNegative: true });
    }

    return ``;
  }

  function labelCell(line: IStatementLine) {
    const isInvoice = [StatementLineType.Invoice, StatementLineType.PaidInvoice].includes(line.type);

    if (isInvoice && line.invoiceId) {
      return (
        <WrappedLink
          to={ `/invoices/${line.invoiceId}` }>
          <Tooltip title={ line.invoiceNumber }>
            <span className={ `underline` }>
              { line.invoiceNumber }
            </span>
          </Tooltip>
          <When condition={ line.frozen }>
            <Tooltip title={ `This invoice has been frozen. It will appear in statements but no other policies will apply.` }>
              <ExclamationCircleIcon className={ `h-5 w-5 ml-1 text-blue-500` } />
            </Tooltip>
          </When>
        </WrappedLink>
      );
    }

    if (line.type === StatementLineType.CreditNote) {
      return (
        <Tooltip title={ `Credit Note` }>
          <span className={ `text-orange-700 flex items-center` }>
            { line.invoiceNumber }

            <Badge
              size={ `xs` }
              color={ `orange` }
              className={ `ml-1` }
            >
              { `C` }
            </Badge>
          </span>
        </Tooltip>
      );
    }

    if (line.type === StatementLineType.Prepayment) {
      return (
        <Tooltip title={ `Prepayment` }>
          <span className={ `text-blue-700 flex items-center` }>
            { line.invoiceNumber }

            <Badge
              size={ `xs` }
              color={ `blue` }
              className={ `ml-1` }
            >
              { `P` }
            </Badge>
          </span>
        </Tooltip>
      );
    }

    if (line.type === StatementLineType.Overpayment) {
      return (
        <Tooltip title={ `Overpayment` }>
          <span className={ `text-green-700 flex items-center` }>
            { line.invoiceNumber }

            <Badge
              size={ `xs` }
              color={ `green` }
              className={ `ml-1` }
            >
              { `O` }
            </Badge>
          </span>
        </Tooltip>
      );
    }

    if (line.type === StatementLineType.Payment) {
      return (
        <span className={ `text-green-700` }>
          { line.invoiceNumber }
        </span>
      );
    }

    if (line.type === StatementLineType.OpeningBalance) {
      return (
        <span className={ `font-semibold` }>
          { line.invoiceNumber }
        </span>
      );
    }

    return (
      <span>
        { line.invoiceNumber }
      </span>
    );
  }

  return (
    <LoadingOverlay
      loading={ loading }
    >
      <CardContent>
        <div className={ `mt-8 flow-root` }>
          <div className={ `-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8` }>
            <div className={ `inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8` }>
              <table className={ `min-w-full divide-y divide-gray-300` }>
                <thead>
                  <tr>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0` }
                    >
                      { `Details` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Reference` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Date` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Due Date` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Amount` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Payments` }
                    </th>
                    <th
                      scope={ `col` }
                      className={ `whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900` }
                    >
                      { `Balance` }
                    </th>
                  </tr>
                </thead>
                <tbody className={ `divide-y divide-gray-200 bg-white` }>
                  { statement.lines.map((line, idx) => (
                    <tr
                      key={ `${line.externalId || line.invoiceId}_${idx}` }
                    >
                      <td className={ `whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-900 sm:pl-0 truncate max-w-[200px]` }>
                        { labelCell(line) }
                      </td>
                      <Tooltip title={ line.invoiceReference }>
                        <td className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-900 truncate max-w-[150px]` }>{ line.invoiceReference }</td>
                      </Tooltip>
                      <td className={ `whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900` }>
                        <When condition={ line.type !== StatementLineType.OpeningBalance }>
                          <DatePopover
                            date={ line.issueDateString }
                            organisationTimezone={ organisationTimezone }
                            labelFormat={ `dd LLL yyyy` }
                          />
                        </When>
                      </td>
                      <td className={ `whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900` }>
                        <When condition={ !!line.invoiceDueDateString }>
                          {
                            () => (
                              <DatePopover
                                date={ line.invoiceDueDateString }
                                organisationTimezone={ organisationTimezone }
                                labelFormat={ `dd LLL yyyy` }
                                time={ `end` }
                              />
                            )
                          }
                        </When>
                      </td>
                      <td className={
                        classNames(
                          `whitespace-nowrap px-2 py-2 text-sm text-gray-500`,
                          line.type === StatementLineType.CreditNote ? `text-orange-700`: ``,
                        )
                      }>
                        { amountCell(line) }
                      </td>
                      <td className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-500` }>
                        { paymentCell(line) }
                      </td>
                      <td className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-700 font-medium` }>
                        <When condition={ line.runningBalanceInCents !== undefined }>
                          { formatAmount(line.runningBalanceInCents ?? 0, line.currency, { bracketsOnNegative: true }) }
                        </When>
                      </td>
                    </tr>
                  )) }
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={ 6 }
                      className={ `whitespace-nowrap py-2 text-sm text-gray-800 font-semibold` }
                    >
                      { `Closing Balance:` }
                    </td>
                    <td
                      className={ `whitespace-nowrap px-2 py-2 text-sm text-gray-800 font-semibold` }
                    >
                      { formatAmount(statement.totalOutstandingInCents, statement.currency, { bracketsOnNegative: true }) }
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        { children }
      </CardContent>
    </LoadingOverlay>
  );
}
